import React, { useEffect, useRef, useState } from 'react';
import { getDeviceLocation } from '../utils/geolocation';
import { ukTodayKey } from '../utils/dates';
import { formatTime } from '../utils/format';
import './JobStagesCard.css';

// The last stage ends the visit: the engineer is home, or has left for the
// next job (Gareth 2026-10-07). The column is still returned_at.
export const ARRIVED_HOME_LABEL = 'Arrived home / changed job';

// Site-work job stages, in order. Columns/sources match travel_records; live
// stamps update the engineer's status: travelling -> on_site -> travelling_home -> available.
export const JOB_STAGES = [
  { key: 'departed', column: 'departed_at', label: 'Job started (departed)' },
  { key: 'arrived', column: 'arrived_at', label: 'Arrived at site' },
  { key: 'departed_site', column: 'departed_site_at', label: 'Finished site work' },
  { key: 'returned', column: 'returned_at', label: ARRIVED_HOME_LABEL }
];

// Under the finish stamps of both Log Work windows.
export const FINISH_STAGES_HINT = `Record Finished site work and ${ARRIVED_HOME_LABEL} before completing. Still on site? Fill everything in and use Finish at home.`;

const listOf = (labels) => (labels.length <= 1
  ? labels.join('')
  : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`);

/**
 * Why a site work log cannot be completed yet, in the server's own words
 * (api utils/jobStages.js missingStagesMessage), or null when it can.
 *
 * Completing - closing, handing over or Carry on tomorrow - needs all four
 * times (Gareth 2026-10-07 and 2026-10-12). Finish at home (waitingForLast)
 * needs the first three: the last one is what it waits for.
 */
export function jobStagesProblem(record, { waitingForLast = false } = {}) {
  const needed = waitingForLast ? JOB_STAGES.slice(0, 3) : JOB_STAGES;
  const missing = needed.filter(stage => !record || !record[stage.column]).map(stage => stage.label);
  if (missing.length === 0) return null;
  if (waitingForLast) return `Record ${listOf(missing)} in Job Stages before using Finish at home.`;
  const onlyLast = missing.length === 1 && missing[0] === ARRIVED_HOME_LABEL;
  return `Record ${listOf(missing)} in Job Stages before completing this work log.`
    + (onlyLast ? ` Still on site? Use Finish at home, and it completes when you record ${ARRIVED_HOME_LABEL}.` : '');
}

/**
 * A trip that is over for Job Stages purposes (Phase 6 step 6): saved with
 * Log Work (linked to a work log), or Finished site work or Arrived home
 * stamped. On a job, the server starts a NEW record for a Job started tap
 * sent without a record id when today's record is one of these.
 */
export function isEarlierTrip(record) {
  return Boolean(record && (record.work_log_id || record.departed_site_at || record.returned_at));
}

export const JOB_STAGES_LOADING_MESSAGE = 'Loading your Job Stages times... Stamping waits until they are shown, so no time is recorded twice.';

/**
 * A typed Job started time is a new trip only when it is after every stamp
 * on the held record; a trip saved with Log Work is never corrected here.
 */
function typedAfterTrip(record, manualAt) {
  if (!record) return true;
  if (record.work_log_id) return true;
  const typed = new Date(manualAt).getTime();
  if (Number.isNaN(typed)) return false;
  const stamps = [record.departed_at, record.arrived_at, record.departed_site_at, record.returned_at]
    .map(value => (value ? new Date(value).getTime() : NaN))
    .filter(value => !Number.isNaN(value));
  return stamps.every(value => typed > value);
}

// Written out by hand for the earlier trip: en-GB Intl prints September as "Sept".
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '21 Sep, 17:05' in UK time. */
const ukDayAndTime = (value) => {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return '';
  const [, month, day] = ukTodayKey(instant).split('-').map(Number);
  return `${day} ${SHORT_MONTHS[month - 1]}, ${formatTime(instant)}`;
};

const stampText = (record, stage, { uk = false } = {}) => {
  const value = record?.[stage.column];
  if (!value) return 'Not recorded';
  const source = record?.[`${stage.key}_source`];
  const hasLocation = record?.[`${stage.key}_lat`] !== null && record?.[`${stage.key}_lat`] !== undefined;
  const when = uk
    ? ukDayAndTime(value)
    : new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return `${when}${source === 'manual' ? ' (manual)' : ''}${hasLocation ? ' 📍' : ''}`;
};

/**
 * Shared Job Stages card for site-work logs on tickets AND scheduled tasks.
 * Stamps hit the API immediately (status + location update can't wait for the
 * work log to be saved); the parent keeps travelRecord state so it can link
 * travel_record_id into the work-log payload on save.
 *
 * Props: authFetch, one of ticketId/taskId, optional workLogId (editing),
 * travelRecord + onTravelRecordChange (parent-held state).
 *
 * Split layout (Gareth 2026-08-12): the form renders TWO instances — the
 * start stamps at the top (before the long check forms) and the finish
 * stamps at the bottom next to the Outcome. `visibleStages` filters which
 * stamps an instance shows; `skipLoad` stops the second instance re-fetching
 * the record the first one already loaded (state is shared via the parent).
 *
 * A second trip to a job on the same day (Phase 6 step 7, jobs only):
 *  - previousTrip: an earlier trip today that is saved with Log Work or over.
 *    It is shown as "Earlier trip today" with its times, in the instance that
 *    shows Job started. Only its Arrived home can still be stamped (when not
 *    yet set), and that stamp names that record's id.
 *  - onPreviousTripChange(record): optional. When given, a record loaded from
 *    /stages/current that is an earlier trip goes here instead of to
 *    onTravelRecordChange (it is not the trip being logged), a stamp on the
 *    earlier trip reports the updated record here, and when a Job started tap
 *    starts a new trip the record it replaces is reported here.
 *  - A Job started tap while the held record is an earlier trip sends no
 *    record id, so the server starts a new trip - only when
 *    onPreviousTripChange is given (the saved Log Work window). A typed Job
 *    started at or before the held trip's last stamp corrects that trip and
 *    names its id, as it always did.
 *
 * waitingForRecord (optional): the parent is still reading the held record's
 * times. Every stamp waits (buttons disabled, a line says so), so a stage
 * that only looks unstamped is never stamped again with a later time.
 *
 * A live tap more than 14 hours after the stage before it is refused by the
 * server with 409 STAMP_TIME_NEEDED (Q4): the sentence is shown and that
 * stage's time box is focused, so the engineer types the time instead.
 *
 * manualOnly (optional): typed times only, no Stamp now. For a visit that is
 * over and being corrected (a reopened work log, 2026-10-13): a tap would
 * record now, days after the visit.
 */
function JobStagesCard({
  authFetch, ticketId, taskId, workLogId, travelRecord, onTravelRecordChange,
  visibleStages = null, skipLoad = false, sectionTitle = 'Job Stages', sectionHint = null,
  previousTrip = null, onPreviousTripChange = null, waitingForRecord = false, manualOnly = false
}) {
  const [stageManualTimes, setStageManualTimes] = useState({});
  const [stampingStage, setStampingStage] = useState(null);
  const [stageError, setStageError] = useState(null);
  // The earlier trip as last stamped here, when the parent does not hold it.
  const [previousTripOverride, setPreviousTripOverride] = useState(null);
  const timeBoxes = useRef({});

  // Jobs only, and never while editing a filed log (that log's own journey).
  const tracksTrips = Boolean(taskId) && !ticketId && !workLogId;

  const stages = visibleStages
    ? JOB_STAGES.filter(stage => visibleStages.includes(stage.key))
    : JOB_STAGES;
  const showsStart = stages.some(stage => stage.key === 'departed');

  useEffect(() => { setPreviousTripOverride(null); }, [previousTrip]);
  const earlierTrip = previousTripOverride || previousTrip;

  useEffect(() => {
    if (skipLoad) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const params = workLogId
          ? `work_log_id=${workLogId}`
          : (ticketId ? `ticket_id=${ticketId}` : `task_id=${taskId}`);
        const response = await authFetch(`/api/v1/travel/stages/current?${params}`);
        if (response.ok) {
          const data = await response.json();
          if (cancelled || !data.data) return;
          if (tracksTrips && onPreviousTripChange && isEarlierTrip(data.data)) {
            onPreviousTripChange(data.data);
          } else {
            onTravelRecordChange(data.data);
          }
        }
      } catch (err) {
        console.error('Failed to load travel stages:', err);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, taskId, workLogId, authFetch, skipLoad]);

  const focusTimeBox = (boxKey) => {
    const box = timeBoxes.current[boxKey];
    if (box && typeof box.focus === 'function') box.focus();
  };

  /**
   * @param {string} stage
   * @param {string|null} manualAt ISO time typed by the engineer, or null for a live tap
   * @param {object} [options]
   * @param {object|null} [options.onRecord] the earlier trip being stamped (its Arrived home)
   */
  const submitStage = async (stage, manualAt = null, { onRecord = null } = {}) => {
    const boxKey = onRecord ? `previous:${stage}` : stage;
    setStampingStage(boxKey);
    setStageError(null);
    try {
      // A new trip: Job started while the held record is an earlier trip, in
      // the saved Log Work window only (it holds the earlier trip). A typed
      // Job started at or before the held trip's last stamp is a correction
      // of that trip, never a new one.
      const startsNewTrip = !onRecord && stage === 'departed' && tracksTrips
        && Boolean(onPreviousTripChange) && isEarlierTrip(travelRecord)
        && (!manualAt || typedAfterTrip(travelRecord, manualAt));
      const recordId = onRecord
        ? onRecord.travel_id
        : (startsNewTrip ? undefined : travelRecord?.travel_id);
      // Live stamps grab device location (best effort - never blocks on farms
      // with no signal); manual retrospective times carry no location.
      const location = manualAt ? null : await getDeviceLocation();
      const response = await authFetch('/api/v1/travel/stages', {
        method: 'POST',
        body: JSON.stringify({
          stage,
          ticket_id: ticketId || undefined,
          task_id: taskId || undefined,
          travel_record_id: recordId || undefined,
          at: manualAt || undefined,
          lat: location?.lat,
          lng: location?.lng,
          accuracy: location?.accuracy
        })
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 409 && data?.code === 'STAMP_TIME_NEEDED') {
          setStageError(data.message || 'Enter the time instead of tapping Stamp now.');
          focusTimeBox(boxKey);
          return;
        }
        throw new Error(data.message || 'Failed to record stage');
      }

      const report = onRecord
        ? (record) => {
          if (onPreviousTripChange) onPreviousTripChange(record);
          else setPreviousTripOverride(record);
        }
        : onTravelRecordChange;

      if (startsNewTrip && travelRecord && onPreviousTripChange) onPreviousTripChange(travelRecord);
      report(data.data);

      // The Returned stamp triggers a background mileage estimate on the
      // server (a routing lookup per leg) - refetch shortly after so the
      // estimated miles appear on the card without a manual refresh.
      if (stage === 'returned' && !manualAt && data.data?.travel_id) {
        setTimeout(async () => {
          try {
            const refreshed = await authFetch(`/api/v1/travel/${data.data.travel_id}`);
            if (refreshed.ok) {
              const refreshedData = await refreshed.json();
              if (refreshedData?.travel_id || refreshedData?.data) {
                report(refreshedData.data || refreshedData);
              }
            }
          } catch {
            // Estimate simply appears next time the record loads.
          }
        }, 8000);
      }
    } catch (err) {
      setStageError(err.message);
    } finally {
      setStampingStage(null);
    }
  };

  const stampControls = (stage, { onRecord = null } = {}) => {
    const boxKey = onRecord ? `previous:${stage.key}` : stage.key;
    const label = onRecord ? `${stage.label} (earlier trip)` : stage.label;
    return (
      <div className="job-stage-actions">
        {!manualOnly && (
          <button
            type="button"
            className="btn-small"
            aria-label={onRecord ? `Stamp ${label} now` : undefined}
            disabled={stampingStage !== null || waitingForRecord}
            onClick={() => submitStage(stage.key, null, { onRecord })}
          >
            {stampingStage === boxKey ? 'Stamping...' : 'Stamp now'}
          </button>
        )}
        <input
          type="datetime-local"
          aria-label={`${label} manual time`}
          ref={element => { timeBoxes.current[boxKey] = element; }}
          value={stageManualTimes[boxKey] || ''}
          onChange={e => setStageManualTimes(prev => ({ ...prev, [boxKey]: e.target.value }))}
        />
        <button
          type="button"
          className="btn-small secondary"
          aria-label={onRecord ? `Set ${label}` : undefined}
          disabled={!stageManualTimes[boxKey] || stampingStage !== null || waitingForRecord}
          onClick={() => submitStage(stage.key, new Date(stageManualTimes[boxKey]).toISOString(), { onRecord })}
        >
          Set
        </button>
      </div>
    );
  };

  const showEarlierTrip = Boolean(earlierTrip) && showsStart && tracksTrips
    && earlierTrip.travel_id !== travelRecord?.travel_id;

  return (
    <div className="form-section job-stages-section">
      <div className="form-section-header">
        <h3>{sectionTitle}</h3>
        <p>
          {sectionHint || 'Stamp each stage as it happens - your live status and location update immediately, even before this log is saved. Times can be entered manually if you had no signal at the time.'}
        </p>
      </div>
      {stageError && <div className="modal-error" role="alert">{stageError}</div>}
      {waitingForRecord && (
        <p className="job-stage-value" role="status">{JOB_STAGES_LOADING_MESSAGE}</p>
      )}

      {showEarlierTrip && (
        <div className="job-stages-grid job-stages-earlier-trip" aria-label="Earlier trip today">
          <p className="job-stage-label">
            Earlier trip today{earlierTrip.work_log_id ? ' (saved with Log Work)' : ''}
          </p>
          {JOB_STAGES.map(stage => {
            const canStamp = stage.key === 'returned' && !earlierTrip.returned_at;
            return (
              <div key={`previous-${stage.key}`} className="job-stage-row">
                <div className="job-stage-info">
                  <span className="job-stage-label">{stage.label}</span>
                  <span className={`job-stage-value ${earlierTrip[stage.column] ? 'recorded' : ''}`}>
                    {stampText(earlierTrip, stage, { uk: true })}
                  </span>
                </div>
                {canStamp && stampControls(stage, { onRecord: earlierTrip })}
              </div>
            );
          })}
          {travelRecord == null && (
            <p className="job-stage-value">Tap Job started below to start today&apos;s next trip.</p>
          )}
        </div>
      )}

      <div className="job-stages-grid">
        {stages.map(stage => {
          const value = travelRecord?.[stage.column];
          const weather = stage.key === 'arrived' ? travelRecord?.arrived_weather : null;
          return (
            <div key={stage.key} className="job-stage-row">
              <div className="job-stage-info">
                <span className="job-stage-label">{stage.label}</span>
                <span className={`job-stage-value ${value ? 'recorded' : ''}`}>
                  {stampText(travelRecord, stage)}
                </span>
                {weather && (
                  <span className="job-stage-weather">
                    {weather.description}, {Math.round(weather.temperature_c)}°C
                    {weather.wind_speed_kmh != null ? `, wind ${Math.round(weather.wind_speed_kmh)} km/h` : ''}
                    {weather.precipitation_mm > 0 ? `, ${weather.precipitation_mm} mm rain` : ''}
                  </span>
                )}
              </div>
              {stampControls(stage)}
            </div>
          );
        })}
      </div>

      {travelRecord?.distance_miles != null && (
        <div className="job-stages-mileage">
          ≈ {Number(travelRecord.distance_miles).toFixed(1)} miles
          {travelRecord.distance_source === 'manual'
            ? ' (entered manually)'
            : ' (estimated route)'}
        </div>
      )}
    </div>
  );
}

export default JobStagesCard;
